import { create } from 'zustand';
import { isPublicDomain } from '@/config/constants';

// ============================================================================
// Auth Store - Backend Session Auth + Public Domain Bypass
// ============================================================================
// Two modes:
// 1. PUBLIC DOMAINS (dominos.co.uk, dominos.ie ONLY):
//    - No login required — isAuthenticated = true automatically
//    - Site behaves as a public customer-facing app
//
// 2. ALL OTHER DOMAINS (pizzaapp-*.2sdtechnologies.com, localhost):
//    - Requires named user login via POST /api/customer/session
//    - Backend authenticates against customer_access_users DB table
//    - Each user has individual credentials — full audit trail
//    - Session token stored in localStorage for subsequent API calls
// ============================================================================

const AUTH_STORAGE_KEY = 'customer-auth-state';
const SESSION_TOKEN_KEY = 'customer-session-token';

const isBrowser = typeof window !== 'undefined';

const readStoredAuth = () => {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Pick<AuthState, 'isAuthenticated' | 'user'>;
  } catch {
    return null;
  }
};

const writeStoredAuth = (payload: Pick<AuthState, 'isAuthenticated' | 'user'>) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors (e.g., Safari private mode)
  }
};

const readSessionToken = (): string | null => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeSessionToken = (token: string) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    // ignore
  }
};

const clearStoredAuth = () => {
  if (!isBrowser) return;
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    window.localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // ignore
  }
};

interface User {
  name: string;
  email: string;
}

interface AuthState {
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
  user: User | null;
  isPublicAccess: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  initialize: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()((set, get) => ({
  isAuthenticated: false,
  loading: false,
  initialized: false,
  user: null,
  isPublicAccess: false,

  // Login via backend session API (POST /api/customer/session)
  login: async (email, password) => {
    if (!email || !password) {
      throw new Error('Please fill in all fields');
    }

    set({ loading: true });
    try {
      const response = await fetch('/api/customer/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Include retry info for rate-limited responses
        if (response.status === 429 && data.retryAfter) {
          throw new Error(data.message || `Too many attempts. Please wait ${data.retryAfter} seconds.`);
        }
        throw new Error(data.message || 'Invalid email or password');
      }

      const data = await response.json();
      const user: User = {
        name: data.user?.name || data.name || email.split('@')[0],
        email: data.user?.email || email,
      };

      // Store session token for subsequent API validation
      if (data.sessionToken) {
        writeSessionToken(data.sessionToken);
      }

      set({ isAuthenticated: true, initialized: true, user });
      writeStoredAuth({ isAuthenticated: true, user });
    } finally {
      set({ loading: false });
    }
  },

  // Logout - clears auth state and backend session
  logout: () => {
    // Fire-and-forget backend session cleanup with stored token
    const token = readSessionToken();
    if (token) {
      fetch('/api/customer/session', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      }).catch(() => {});
    }

    set({
      isAuthenticated: false,
      initialized: true,
      user: null,
      isPublicAccess: false,
    });
    clearStoredAuth();
  },

  // Initialize auth state
  initialize: () => {
    const state = get();
    if (state.initialized) return;

    // PUBLIC DOMAIN CHECK: dominos.co.uk / dominos.ie ONLY → auto-authenticate
    // No login wall, site is fully public
    if (isPublicDomain()) {
      set({
        isAuthenticated: true,
        initialized: true,
        isPublicAccess: true,
        user: { name: 'Customer', email: '' },
      });
      return;
    }

    // BRANDED DOMAIN: check for stored session + token
    const stored = readStoredAuth();
    const token = readSessionToken();
    if (stored?.isAuthenticated && stored.user && token) {
      // Optimistically unblock the UI — assume the stored session is still valid.
      // Validate in the background; revoke access immediately if the token is rejected.
      set({ isAuthenticated: true, user: stored.user, initialized: true });

      fetch('/api/customer/session/validate', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      })
        .then((res) => {
          if (!res.ok) {
            clearStoredAuth();
            set({ isAuthenticated: false, user: null });
          }
        })
        .catch(() => {
          clearStoredAuth();
          set({ isAuthenticated: false, user: null });
        });
      return;
    }

    // No stored auth or missing token — redirect to login
    set({ initialized: true, isAuthenticated: false, user: null });
  },
}));

// Selector hooks for optimized re-renders
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
export const selectAuthLoading = (state: AuthStore) => state.loading;
export const selectUser = (state: AuthStore) => state.user;
export const selectIsPublicAccess = (state: AuthStore) => state.isPublicAccess;
